import React, { useCallback, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { Currency as CurrencyField } from 'gw-components-platform-react';
import { DateField } from '@jutro/components';
import CoveragesComponent from '../CoveragesComponent/CoveragesComponent';
import metadata from './PolicyLiabilityComponent.metadata.json5';
import {NumberValue, formatNumber} from '@jutro/components';
import { IntlContext } from '@jutro/locale';

const getCell = (rowData, index, property) => {
    const rowValue = rowData[property.id];
    return (
        <span>
            {rowValue}
        </span>
    );
};

const getCurrencyCell = (rowData, rowIndex, property, intl) => {
    const rowValue = (rowData[property.id] && rowData[property.id].amount != undefined) ? formatNumber(intl, { format: 'decimal', showFractions: true}, (rowData[property.id].amount)) : '-'
    
    if(_.isNaN(rowData[property.id]?.amount) || (rowData[property.id]?.amount == 0 || rowValue == '-')){
        return(<b><span>{rowValue}</span></b>)
    }else{
        return(
        <b>
            <NumberValue
                format="decimal"
                id={`premium_${rowIndex}`}
                maximumFractionDigits={2}
                placeholder="-"
                prefix=""
                showFractions
                showGrouping
                suffix=".00"
                tag="div"
                value={rowValue}            
            />
        </b>
        )
    }
        
};


const getDateCell = (rowData, rowIndex, property) => {
    const rowValue = rowData[property.id];
    return (
        <DateField
            id={`${property.id}_${rowIndex}`}
            value={rowValue}
            format="short"
            dataType={_.isString(rowValue) ? 'string' : 'object'}
            readOnly
            hideLabel
            layout="full-width"
            showOptional={false}
        />
    );
};

function PolicyLiabilityComponent(props) {
    const { value: policyData, updateLiabilityPremium } = props;    
    const intl = useContext(IntlContext);
    if (_.isEmpty(policyData) || policyData === undefined) {
        return null;
    }
    const { productName } = policyData.product || policyData.baseData;

    const getFarmLiabilityCoverages = (liablityLineCoverages) => {
        return liablityLineCoverages == undefined ? []: liablityLineCoverages.filter((coverage) => {
            return coverage.selected;
        });
    };

    const policyLiabilityCoverables = useMemo(() => {
        let liabilityCoverages = [];
        // eslint-disable-next-line default-case
        switch (productName) {
            case 'Residential':
                liabilityCoverages = policyData.lobs ? policyData.lobs.homeOwners.liabilityCoverageDTOs : policyData.lobData.homeowners.offerings[0].coverages.liablityLineCoverages;
                break;
            case 'Farm':
                liabilityCoverages = policyData.lobs ? getFarmLiabilityCoverages(policyData.lobs.farm.liablityLineCoverages) : (policyData.lobData.farm ? policyData.lobData.farm.offerings[0].coverages.liablityLineCoverages : []);
                break;
            case 'Commercial Property':
                liabilityCoverages = policyData.lobs ? policyData.lobs.commercialProperty.liabilityCoverageDTOs : policyData.lobData.businessOwners.coverages.liablityLineCoverages;
                break;
        }
        return liabilityCoverages;
    }, [policyData]);

    const getLiabilityPremiumValue = (data) => {
        return data.liabilityLinePremium ? data.liabilityLinePremium.amount : 0
    }

    const policyLiabilityExtensions = useMemo(() => {
        let liabilityExtensions = [];
        let liabilityPremium = '';

        // eslint-disable-next-line default-case
        switch (productName) {
            case 'Residential':
                liabilityExtensions = policyData.lobs ? policyData.lobs.homeOwners.liabilityExtensions : policyData.lobData.homeowners.offerings[0].coverages.liabilityExtensions;
                liabilityPremium = (policyData.lobs) ? getLiabilityPremiumValue(policyData.lobs.homeOwners) : getLiabilityPremiumValue(policyData.lobData.homeowners.offerings[0].coverages)  ;
                break;
                // uncomment below line if liability extension for farm is applicable
            case 'Farm':
                liabilityExtensions = policyData.lobs ? policyData.lobs.farm.liabilityExtensions : (policyData.lobData.farm ? policyData.lobData.farm.offerings[0].coverages.liabilityExtensions : []);
                liabilityPremium = (policyData.lobs) ? getLiabilityPremiumValue(policyData.lobs.farm) : (policyData.lobData.farm ? getLiabilityPremiumValue(policyData.lobData.farm.offerings[0].coverages)  : []);
                break;
            case 'Commercial Property':
                liabilityExtensions = policyData.lobs ? policyData.lobs.commercialProperty.liabilityExtensions : policyData.lobData.businessOwners.coverages.liabilityExtensions;
                liabilityPremium = (policyData.lobs) ? getLiabilityPremiumValue(policyData.lobs.commercialProperty)  : getLiabilityPremiumValue(policyData.lobData.businessOwners.coverages)  ;
                break;
        }
        if(!_.isUndefined(updateLiabilityPremium)){updateLiabilityPremium(liabilityPremium)}
        return liabilityExtensions;
    }, [policyData, updateLiabilityPremium]);

    const generateLiabilityExtensionsTableOverrides = useCallback(() => {
        return {
            extAmountColumn: { onCell: getCurrencyCell },
            nameColumn: { onCell: getCell },
            effDateColumn: { onCell: getDateCell },
            expDateColumn: { onCell: getDateCell },
            prorationColumn: { onCell: getCell },
            extPremiumColumn: { onCell: (rowData, rowIndex, property) => {return getCurrencyCell(rowData, rowIndex, property, intl) }}, 
            valueDescriptionColumn: { onCell: getCell},
            descriptionValueColumn: { onCell: getCell}
        };
    }, []);

    const overrides = {
        liabilityTableComp: {
            value: policyLiabilityCoverables
        },
        PolicyLiabilityExtensions:{
            visible: !_.isEmpty(policyLiabilityExtensions)
        },
        policyLiabilityExtensionsTable: {
            data: policyLiabilityExtensions,
            visible: !_.isEmpty(policyLiabilityExtensions),
            //visible: policyLiabilityExtensions && productName === 'Residential'
            
        },
        FarmliabilityExtensionsTableComp: {
            value: policyLiabilityExtensions,
            visible: false,
            //visible: !_.isEmpty(policyLiabilityExtensions) && productName === 'Farm'
        },
        noLiabilityLabel: {
            visible: _.isEmpty(policyLiabilityExtensions) && _.isEmpty(policyLiabilityCoverables)
        },
        ...generateLiabilityExtensionsTableOverrides()
    };

    const resolveComponentMap = {
        coveragescomponent: CoveragesComponent
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={policyData}
            componentMap={resolveComponentMap}
            overrideProps={overrides}
        />
    );
}


PolicyLiabilityComponent.propTypes = {
    value: PropTypes.shape({})
};

PolicyLiabilityComponent.defaultProps = {
    value: {
        policyData: {}
    }
};

export default PolicyLiabilityComponent;
